import { useEffect, useMemo, useState } from "react";
import { ChevronDown, History, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLocalization } from "@/context/localization-context";
import { shortDateTime } from "@/lib/date-format";
import { cn } from "@/lib/utils";
import { catalogWrites, classifyWriteError, useCatalogWrite, useRecipeVersions, type WriteError } from "../api";
import { componentDiff, RECORD_ID, sameRecipe, sameStrings, slug } from "../model";
import type { CatalogIngredient, CatalogRecipe, RecipeDraft } from "../types";
import { EditorFooter, Field, IngredientChip, IngredientPicker } from "./shared";

interface Row {
  key: number;
  ingredient_id: string | null;
  after_baking: boolean;
}

let rowKey = 0;
const toRows = (recipe: CatalogRecipe | null): Row[] =>
  recipe
    ? recipe.components.map((c) => ({ key: ++rowKey, ingredient_id: c.ingredient_id, after_baking: !!c.after_baking }))
    : [{ key: ++rowKey, ingredient_id: null, after_baking: false }];

/**
 * Creates a recipe (``recipe`` null) or saves a new version of one. The version the edit started from
 * goes with the save: when somebody saved another one meanwhile, the backend answers 409 and the editor
 * offers to load it.
 */
export function RecipeEditor({
  open,
  onOpenChange,
  recipe,
  ingredients,
  recipeIds,
  onReload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** the current version from the catalog; null = a new recipe */
  recipe: CatalogRecipe | null;
  ingredients: CatalogIngredient[];
  /** ids in use (a new recipe needs another one) */
  recipeIds: Set<string>;
  onReload: () => Promise<unknown>;
}) {
  const { t } = useLocalization();
  const write = useCatalogWrite();
  const isNew = recipe === null;

  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [name, setName] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [ignore, setIgnore] = useState<string[]>([]);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [error, setError] = useState<WriteError | null>(null);
  const [resetPending, setResetPending] = useState(false);

  const reset = (from: CatalogRecipe | null) => {
    setId(from?.id ?? "");
    setIdTouched(false);
    setName(from?.name ?? "");
    setRows(toRows(from));
    setIgnore(from?.extra_ignore ?? []);
    setBaseVersion(from?.version ?? null);
    setError(null);
  };

  // a fresh draft every time the dialog opens
  useEffect(() => {
    if (open) reset(recipe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // "load the current version" after a conflict: the catalog was fetched again
  useEffect(() => {
    if (resetPending && recipe && recipe.version !== baseVersion) {
      reset(recipe);
      setResetPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe, resetPending]);

  const byId = useMemo(() => new Map(ingredients.map((i) => [i.id, i])), [ingredients]);
  const taken = useMemo(() => new Set(rows.map((r) => r.ingredient_id).filter(Boolean) as string[]), [rows]);
  const draft: RecipeDraft = {
    name: name.trim(),
    components: rows
      .filter((r) => r.ingredient_id)
      .map((r) => ({ ingredient_id: r.ingredient_id as string, after_baking: r.after_baking })),
    extra_ignore: ignore,
  };
  // look-alikes worth ignoring: detectable ingredients the recipe does not have
  const ignorable = useMemo(
    () => ingredients.filter((i) => i.detector_classes.length > 0 && !taken.has(i.id) && !ignore.includes(i.id)),
    [ingredients, taken, ignore],
  );

  const effectiveId = isNew ? id.trim() : recipe.id;
  const idProblem = !isNew
    ? null
    : !effectiveId
      ? t("catalog.recipe.idMissing")
      : !RECORD_ID.test(effectiveId)
        ? t("catalog.recipe.idInvalid")
        : recipeIds.has(effectiveId)
          ? t("catalog.recipe.idTaken")
          : null;
  const duplicate = rows.some((r, i) => r.ingredient_id && rows.findIndex((o) => o.ingredient_id === r.ingredient_id) !== i);
  const incomplete = rows.some((r) => !r.ingredient_id);
  const unchanged =
    !isNew && sameRecipe(draft, { name: recipe.name, components: recipe.components, extra_ignore: recipe.extra_ignore ?? [] });
  const canSave = !!draft.name && !idProblem && draft.components.length > 0 && !duplicate && !incomplete && !unchanged;

  const setRow = (key: number, patch: Partial<Row>) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const save = async () => {
    setError(null);
    try {
      const result = await write.mutateAsync(
        isNew ? catalogWrites.createRecipe(effectiveId, draft) : catalogWrites.updateRecipe(recipe.id, draft, baseVersion ?? recipe.version),
      );
      const saved = result.recipes.find((r) => r.id === effectiveId);
      toast.success(t("catalog.recipe.saved", { name: draft.name, version: saved?.version ?? "?" }));
      onOpenChange(false);
    } catch (e) {
      setError(classifyWriteError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !write.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-2xl gap-5">
        <DialogHeader className="text-left">
          <DialogTitle className="pr-6">
            {isNew ? t("catalog.recipe.newTitle") : t("catalog.recipe.editTitle", { name: recipe.name })}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? t("catalog.recipe.newDescription")
              : t("catalog.recipe.editDescription", { id: recipe.id, version: recipe.version })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
          <Field label={t("catalog.recipe.name")} htmlFor="recipe-name">
            <Input
              id="recipe-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (isNew && !idTouched) setId(slug(event.target.value));
              }}
              autoComplete="off"
            />
          </Field>
          <Field label={t("catalog.recipe.id")} htmlFor="recipe-id" hint={isNew ? idProblem ?? undefined : undefined}>
            <Input
              id="recipe-id"
              value={isNew ? id : recipe.id}
              disabled={!isNew}
              onChange={(event) => {
                setId(event.target.value);
                setIdTouched(true);
              }}
              className="font-mono"
              autoComplete="off"
            />
          </Field>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t("catalog.recipe.components")}</span>
            {/* the rows label their checkbox from sm up */}
            <span className="text-xs text-muted-foreground sm:hidden">{t("catalog.recipe.afterBakingColumn")}</span>
          </div>
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <li key={row.key} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2">
                <IngredientPicker
                  ingredients={ingredients}
                  value={row.ingredient_id}
                  onChange={(ingredientId) => setRow(row.key, { ingredient_id: ingredientId })}
                  taken={taken}
                  placeholder={t("catalog.recipe.pickIngredient")}
                />
                <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-md px-1.5 text-xs text-muted-foreground hover:text-foreground">
                  <Checkbox
                    checked={row.after_baking}
                    onCheckedChange={(checked) => setRow(row.key, { after_baking: checked === true })}
                    aria-label={t("catalog.recipe.afterBaking")}
                  />
                  <span className="hidden sm:inline">{t("catalog.recipe.afterBaking")}</span>
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-9 w-9 p-0 text-muted-foreground"
                  onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                  aria-label={t("catalog.recipe.removeComponent")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setRows((prev) => [...prev, { key: ++rowKey, ingredient_id: null, after_baking: false }])}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t("catalog.recipe.addComponent")}
            </Button>
            {duplicate && <span className="text-xs text-red-300">{t("catalog.recipe.duplicate")}</span>}
          </div>
          <p className="text-xs text-muted-foreground">{t("catalog.recipe.detectableHint")}</p>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-xs font-medium text-muted-foreground">{t("catalog.recipe.extraIgnore")}</span>
          {ignore.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {ignore.map((ingredientId) => (
                <span key={ingredientId} className="inline-flex max-w-full items-center gap-0.5">
                  <IngredientChip ingredient={byId.get(ingredientId)} fallbackId={ingredientId} />
                  <button
                    type="button"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={() => setIgnore((prev) => prev.filter((id) => id !== ingredientId))}
                    aria-label={t("catalog.tags.remove", { value: byId.get(ingredientId)?.name ?? ingredientId })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="max-w-xs">
            <IngredientPicker
              ingredients={ignorable}
              value={null}
              onChange={(ingredientId) => setIgnore((prev) => [...prev, ingredientId])}
              placeholder={t("catalog.recipe.extraIgnoreAdd")}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t("catalog.recipe.extraIgnoreHint")}</p>
        </div>

        {!isNew && <VersionHistory recipe={recipe} byId={byId} />}

        <EditorFooter
          error={error}
          onReload={() => {
            setResetPending(true);
            void onReload();
          }}
          saving={write.isPending}
          canSave={canSave}
          onCancel={() => onOpenChange(false)}
          onSave={() => void save()}
          hint={t("catalog.recipe.appliesHint")}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Every version of the recipe with what changed against the one before. */
function VersionHistory({ recipe, byId }: { recipe: CatalogRecipe; byId: Map<string, CatalogIngredient> }) {
  const { t, tp } = useLocalization();
  const [open, setOpen] = useState(false);
  const versions = useRecipeVersions(open ? recipe.id : null);
  const items = versions.data?.items ?? [];

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border">
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-muted-foreground hover:text-foreground">
          <History className="h-3.5 w-3.5" aria-hidden />
          <span className="flex-1">{tp("catalog.recipe.versions", recipe.version)}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} aria-hidden />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="flex flex-col divide-y border-t">
          {versions.isLoading && <li className="px-3 py-2 text-xs text-muted-foreground">{t("catalog.loading")}</li>}
          {items.map((version, index) => {
            const older = items[index + 1];
            const diff = older ? componentDiff(older.components, version.components) : null;
            return (
              <li key={version.version} className="flex min-w-0 flex-col gap-1.5 px-3 py-2">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
                  <span className="font-semibold">{t("catalog.recipe.version", { version: version.version })}</span>
                  <span className="text-muted-foreground">
                    {version.valid_from
                      ? t("catalog.recipe.validFrom", { at: shortDateTime(version.valid_from) })
                      : t("catalog.recipe.validAlways")}
                    {version.valid_to && ` – ${shortDateTime(version.valid_to)}`}
                  </span>
                  <span className="min-w-0 break-all text-muted-foreground">· {version.created_by}</span>
                </div>
                {diff ? (
                  <div className="flex flex-wrap gap-1">
                    {diff.added.map((id) => (
                      <span key={`+${id}`} className="inline-flex items-center gap-1 text-xs text-emerald-300">
                        +<IngredientChip ingredient={byId.get(id)} fallbackId={id} />
                      </span>
                    ))}
                    {diff.removed.map((id) => (
                      <span key={`-${id}`} className="inline-flex items-center gap-1 text-xs text-red-300">
                        −<IngredientChip ingredient={byId.get(id)} fallbackId={id} className="line-through" />
                      </span>
                    ))}
                    {diff.moved.map((id) => (
                      <span key={`~${id}`} className="inline-flex items-center gap-1 text-xs text-amber-300">
                        ~<IngredientChip
                          ingredient={byId.get(id)}
                          fallbackId={id}
                          afterBaking={!!version.components.find((c) => c.ingredient_id === id)?.after_baking}
                        />
                      </span>
                    ))}
                    {older && !sameStrings(older.extra_ignore ?? [], version.extra_ignore ?? []) && (
                      <span className="text-xs text-muted-foreground">
                        {t("catalog.recipe.extraIgnoreChanged", {
                          names: (version.extra_ignore ?? []).map((id) => byId.get(id)?.name ?? id).join(", ") || "–",
                        })}
                      </span>
                    )}
                    {older && older.name !== version.name && (
                      <span className="text-xs text-muted-foreground">
                        {t("catalog.recipe.renamed", { from: older.name, to: version.name })}
                      </span>
                    )}
                    {!diff.added.length &&
                      !diff.removed.length &&
                      !diff.moved.length &&
                      older?.name === version.name &&
                      sameStrings(older.extra_ignore ?? [], version.extra_ignore ?? []) && (
                      <span className="text-xs text-muted-foreground">{t("catalog.recipe.orderOnly")}</span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {version.components.map((c) => (
                      <IngredientChip key={c.ingredient_id} ingredient={byId.get(c.ingredient_id)} fallbackId={c.ingredient_id} afterBaking={c.after_baking} />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}
